import { motion } from 'framer-motion';
import React from 'react';

const features = [
  {
    title: 'Even Low Quantities @ Best Prices',
    description: 'We offer low/ single product quantities at affordable prices.',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    title: 'High Quality Products and Easy Design',
    description: 'Our wide selection of high-quality products and online design tools make it easy for you to customize and order your favourite products.',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
  },
  {
    title: 'Free Replacement or Full Refund',
    description: 'We stand by everything we sell. So if you\'re not satisfied, we\'ll make it right.',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
];

const AboutUs = () => {
  return (
    <div className="bg-[#f3f5f3]" style={{ fontFamily: '"Bricolage Grotesque", sans-serif' }}>
      <section className="py-8 sm:py-12 lg:py-16 xl:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Hero Section */}
            <motion.div
              className="text-center mb-8 sm:mb-12 lg:mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-2 sm:mb-3 lg:mb-4">
                Kigo Prints
              </h1>
              <p className="text-base sm:text-lg lg:text-xl text-[#008000] font-medium mb-4 sm:mb-6 lg:mb-8">
                The Leader in Customisation
              </p>
              <p className="text-sm sm:text-base lg:text-lg text-gray-600 leading-relaxed text-justify px-2 sm:px-3">
                For more than 20 years, Kigo Prints has helped business owners, entrepreneurs and individuals create their identities with custom designs and professional marketing. Our online printing services are intended to help you find high quality customised products you need – visiting cards, personalized clothing, gifting products, and much more.
              </p>
            </motion.div>

            {/* Mission & Vision */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 mb-8 sm:mb-12 lg:mb-16">
              <motion.div
                className="bg-white p-4 sm:p-6 lg:p-8 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                whileHover={{ y: -5 }}
              >
                <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-gray-900 mb-2 sm:mb-3 lg:mb-4">Our Mission</h2>
                <p className="text-sm sm:text-base text-gray-600">
                  To empower businesses and individuals with high-quality, customized printing solutions that bring their creative visions to life while maintaining affordability and excellence in service.
                </p>
              </motion.div>
              <motion.div
                className="bg-white p-4 sm:p-6 lg:p-8 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300"
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                whileHover={{ y: -5 }}
              >
                <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-gray-900 mb-2 sm:mb-3 lg:mb-4">Our Vision</h2>
                <p className="text-sm sm:text-base text-gray-600">
                  To be India's most trusted and innovative printing partner, setting new standards in quality, customization, and customer satisfaction in the printing industry.
                </p>
              </motion.div>
            </div>

            {/* Features Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 mb-8 sm:mb-12 lg:mb-16">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  className="bg-white rounded-xl p-4 sm:p-5 lg:p-6 shadow-sm hover:shadow-lg transition-all duration-300"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                >
                  {/* <div className="text-[#008000] mb-4">{feature.icon}</div> */}
                  <h3 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600">{feature.description}</p>
                </motion.div>
              ))}
            </div>

            {/* Why Choose Us */}
            <motion.div
              className="bg-white p-4 sm:p-6 lg:p-8 rounded-xl shadow-sm mb-8 sm:mb-12 lg:mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-gray-900 mb-4 sm:mb-5 lg:mb-6 text-center">Why Choose Kigo Prints?</h2>
              <div className="space-y-2 sm:space-y-3 lg:space-y-4">
                <p className="text-sm sm:text-base text-gray-600">
                  <span className="font-medium text-[#008000]">✓</span> Wide range of customizable products from business cards to large format printing
                </p>
                <p className="text-sm sm:text-base text-gray-600">
                  <span className="font-medium text-[#008000]">✓</span> User-friendly online design tools with professional templates
                </p>
                <p className="text-sm sm:text-base text-gray-600">
                  <span className="font-medium text-[#008000]">✓</span> Competitive pricing with no compromise on quality
                </p>
                <p className="text-sm sm:text-base text-gray-600">
                  <span className="font-medium text-[#008000]">✓</span> Fast turnaround times and reliable delivery
                </p>
                <p className="text-sm sm:text-base text-gray-600">
                  <span className="font-medium text-[#008000]">✓</span> Dedicated customer support team
                </p>
              </div>
            </motion.div>

            {/* Contact Section */}
            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <p className="text-sm sm:text-base lg:text-lg text-gray-600 mb-4 sm:mb-5 lg:mb-6 px-2">
                Have questions or need assistance? Our customer support team is here to help!
              </p>
              <motion.a
                href="tel:+918080184435"
                className="inline-block px-6 sm:px-7 lg:px-8 py-2.5 sm:py-3 lg:py-4 bg-[#008000] text-white rounded-xl hover:bg-[#008000] transition-all duration-300 text-sm sm:text-base lg:text-lg font-medium shadow-md"
                whileHover={{ scale: 1.05, boxShadow: "0 10px 25px rgba(0,0,0,0.1)" }}
                whileTap={{ scale: 0.98 }}
              >
                Contact Us
              </motion.a>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;